import { z } from 'zod';

/**
 * API error codes (D-011; the table in docs/plan/30-api.md → API conventions → Errors). Every
 * error response is `{ "error": { "code": "<snake_case>", "message": "...", "details"?: {...} } }`
 * and each code has exactly one HTTP status, listed here. Add module-specific codes here as
 * endpoints are built (e.g. `invite_expired: 410`), never as ad hoc strings in handlers.
 */
export const ERROR_STATUS = {
  // 400 — the request has the wrong shape
  /** Zod failure. `details.issues[] = { path, code, message }`. */
  validation_failed: 400,
  /** The body cannot be parsed as JSON (or is empty with a JSON content type). */
  invalid_json: 400,
  /** The cursor was tampered with, or it was issued for different filters. */
  invalid_cursor: 400,
  /** A route that requires `Idempotency-Key` was called without one. */
  idempotency_key_required: 400,
  /** Fallback for other malformed requests rejected by Fastify or a plugin. Prefer a specific code. */
  bad_request: 400,

  // 401
  /** Bearer token missing, malformed or with a bad signature. */
  unauthenticated: 401,
  /** Valid signature but expired: the client refreshes, then retries once. */
  access_token_expired: 401,
  /** The session was revoked, or it passed idle or absolute expiry. */
  session_revoked: 401,
  /**
   * The membership has been deactivated. 401 on authenticated requests. PLAN §4 reuses the code
   * with other statuses, which callers pass explicitly (`new AppError(code, msg, { status })`):
   * `POST /auth/login` answers 403 (password correct, membership deactivated) and
   * `POST /invites` answers 409 (reactivate instead). See D-113.
   */
  member_deactivated: 401,
  /** `POST /auth/login`: unknown email or wrong password (indistinguishable). */
  invalid_credentials: 401,
  /** `POST /auth/refresh`: a just-rotated refresh token reused within 30 s (D-008). */
  refresh_race: 401,

  // 403
  /** Visible but not allowed. `details.permission` names the matrix action. */
  forbidden: 403,
  /** Bot token lacks a scope. `details.required` lists the missing scopes. */
  insufficient_scope: 403,
  /** The route is not bot-callable. */
  bot_not_allowed: 403,
  /** A cookie route was called without an allowed `Origin` or without `X-Hub-CSRF: 1`. */
  csrf_failed: 403,

  // 404 / 405 / 406
  /** Unknown route or resource, or one the caller may not see. Prefer `<resource>_not_found`. */
  not_found: 404,
  method_not_allowed: 405,
  /** Fallback for 406 responses from plugins (e.g. a multipart route called without multipart). */
  not_acceptable: 406,

  // 409 / 412
  /** The same `Idempotency-Key` is still executing. Sent with `Retry-After: 1`. */
  idempotency_in_progress: 409,
  /** A state-machine action is not allowed. `details = { from, action, allowedActions }`. */
  invalid_transition: 409,
  /** `If-Match` does not match the current `version`. `details.currentVersion`. */
  version_conflict: 412,

  // 413 / 415 / 422 / 426
  payload_too_large: 413,
  unsupported_media_type: 415,
  /** The same `Idempotency-Key` was sent with a different request body or route. */
  idempotency_key_reused: 422,
  /** The native client is below the minimum version. `details.minVersion`. */
  client_upgrade_required: 426,

  // 429
  /** `details = { limit, windowSeconds, retryAfterSeconds }`, plus a `Retry-After` header. */
  rate_limited: 429,

  // 5xx
  /** Generic message; reported to Sentry when SENTRY_DSN is set. */
  internal_error: 500,
  /**
   * The route exists (its contract and permissions are fixed) but the feature behind it is not
   * built yet in this build. Phase 3 seams answer it until their workstream lands (D-500).
   */
  not_implemented: 501,
  /** A dependency is down or the process is draining. Sent with `Retry-After`. */
  service_unavailable: 503,

  // ------------------------------------------------------------ Phase 0 — auth (PLAN §4)
  /** Login: password correct but no active membership (in the requested workspace, or any). */
  no_active_membership: 403,
  /** An OTP is required but the user already had 5 sends in the last hour. */
  otp_send_limit: 429,
  /** Wrong OTP code. `details.attemptsRemaining` (0–4). */
  otp_invalid: 401,
  otp_challenge_not_found: 404,
  /** The code is older than 10 minutes; resend. */
  otp_expired: 410,
  /** 5 failed attempts; start again at login. */
  otp_challenge_locked: 410,
  /** The challenge is older than 30 minutes, or was already used. */
  otp_challenge_expired: 410,
  /** Within 30 s of the last send. `details.retryAfterSeconds`. */
  otp_resend_cooldown: 429,
  /** Refresh: no cookie and no body token. */
  refresh_missing: 401,
  /** Refresh: unknown token. */
  refresh_invalid: 401,
  /** Refresh: reused more than 30 s after rotation; the session is now revoked (D-008). */
  refresh_reused: 401,
  /** Wrong password-reset code. `details.attemptsRemaining`. */
  reset_code_invalid: 401,
  reset_not_found: 404,
  reset_expired: 410,
  reset_locked: 410,
  /** `details.reasons` ⊆ `too_short`, `too_long`, `common_password` (D-008). */
  password_policy_violation: 422,
  current_password_incorrect: 422,
  password_unchanged: 422,
  session_not_found: 404,

  // ------------------------------------------------------------ Phase 0 — invites (D-022)
  invite_not_found: 404,
  invite_expired: 410,
  invite_revoked: 410,
  invite_already_accepted: 409,
  /** The invite belongs to a different email than the signed-in or signing-in user. */
  invite_email_mismatch: 403,
  /** Accepted or revoked invites cannot be resent. `details.status`. */
  invite_not_pending: 409,
  /** `details.retryAfterSeconds`. */
  invite_resend_cooldown: 429,
  /** `POST /invites/accept`: a user with this email exists; use the existing-user path. */
  account_exists: 409,
  /** The invitee is already an active member. `details.userId`. */
  already_member: 409,
  /** For example an admin inviting an owner, or a manager inviting an admin (D-006). */
  role_not_grantable: 403,
  email_domain_not_allowed: 422,

  // ------------------------------------------------------------ Phase 0 — workspace and members
  invalid_timezone: 422,
  /** `details.ratio`: the accent colour must reach 4.5:1 against white (WCAG AA). */
  accent_contrast_too_low: 422,
  invalid_phone: 422,
  member_not_found: 404,
  /** Nobody changes their own role (D-006). */
  cannot_change_own_role: 403,
  cannot_deactivate_self: 403,
  /** The change touches Owner status, or targets an Owner, and the caller is not an Owner. */
  owner_required: 403,
  /** The last active Owner cannot be demoted or deactivated (D-006). */
  last_owner: 409,
  /** `details.chain` (user ids): the change would make someone their own manager (D-007). */
  manager_cycle: 422,
  /** The manager is deactivated, a guest, the person themselves or not in this workspace. */
  manager_not_eligible: 422,
  /** Another member of the workspace has that payroll employee code (D-930). */
  employee_code_taken: 409,

  // ------------------------------------------------------------ Phase 0 — departments
  department_not_found: 404,
  department_archived: 409,
  department_name_taken: 409,
  /** `details.memberCount` (archive) or `details.references` (delete). */
  department_in_use: 409,

  // ------------------------------------------------------------ Phase 0 — profile fields
  profile_field_not_found: 404,
  /** `details.fieldId`. */
  profile_field_not_editable: 403,
  /** `details = { fieldId, reason }`. */
  profile_field_value_invalid: 422,
  profile_field_limit: 409,
  profile_field_label_taken: 409,
  option_removal_unconfirmed: 422,
  order_mismatch: 422,

  // ------------------------------------------------------------ Phase 0 — avatars and local files
  file_missing: 400,
  image_unprocessable: 422,
  image_too_small: 422,
  invalid_crop: 422,
  /** Local-driver signed URL with a bad signature. */
  signature_invalid: 403,
  /** Local-driver signed URL past its `exp`. */
  url_expired: 410,

  // ------------------------------------------------------------ Phase 1 — idempotency (PLAN §4)
  /** Message send needs a UUIDv4/UUIDv7 `Idempotency-Key` (it becomes `clientMsgId`, D-015). */
  idempotency_key_invalid: 400,

  // ------------------------------------------------------------ Phase 1 — conversations
  /** The conversation does not exist or the caller may not see it (private, DM, other tenant). */
  conversation_not_found: 404,
  /** D-1157: the canvas moved on since the editor opened it; re-read and merge. */
  canvas_version_conflict: 409,
  channel_name_taken: 409,
  /** `details.reason`. Names: lowercase letters in any script, digits, `-`, `_`, 1–80. */
  invalid_channel_name: 422,
  /** Group DMs hold at most 9 people including the creator. `details.max`. */
  too_many_participants: 422,
  /** Archived channels are read-only. */
  conversation_archived: 409,
  /** The action exists only for channels (rename, archive, roles, …). */
  not_supported_for_dm: 422,
  cannot_archive_general: 409,
  /** `POST /conversations/:id/convert`: `confirmName` is not the channel name. */
  confirmation_mismatch: 422,
  cannot_convert_general: 409,
  cannot_leave_general: 409,
  cannot_leave_dm: 422,
  /** A single-channel guest would be in more than one channel (`enforce_guest_scope`). */
  guest_channel_limit: 409,
  /** DMs and group DMs have a fixed member set; adding someone opens a new group DM. */
  cannot_add_to_dm: 422,
  cannot_remove_from_general: 409,
  /** Default channels must be public channels. `details.conversationId`. */
  not_public_channel: 422,
  /** The caller can read the conversation but is not a member (posting, reacting, prefs). */
  not_conversation_member: 403,
  /**
   * A guest invite from before Phase 1 names no channels, so it cannot be re-sent: invite the
   * person again and choose their channels (D-151).
   */
  guest_channels_required: 422,

  // ------------------------------------------------------------ Phase 1 — messages
  message_not_found: 404,
  /** `@here`/`@channel` would notify more than `messaging.broadcastWarnThreshold` people. `details.recipientCount`. */
  broadcast_confirmation_required: 409,
  /** A thread root must be a non-deleted top-level message of the same conversation. */
  invalid_thread_root: 422,
  not_a_thread_root: 422,
  /** `details = { limit, kind: 'plain_text' | 'content_bytes' }`. */
  message_too_long: 422,
  /** Neither text nor files. */
  message_empty: 422,
  /** More than 50 mentions. `details.max`. */
  too_many_mentions: 422,
  /** The request sends both `content` and `text` (or neither where one is required). */
  body_ambiguous: 422,
  /** `content` is outside the shared schema (D-045). `details = { reason, path }`. */
  invalid_rich_text: 422,
  not_author: 403,
  message_deleted: 409,
  /** `messaging.editWindowMinutes` has passed. */
  edit_window_closed: 409,
  /** D-1176: somebody has read it, so the words cannot be swapped under them. */
  message_seen: 409,
  /** `GET /conversations/:id/messages`: more than one of beforeSeq / afterSeq / aroundSeq. */
  anchors_conflict: 400,
  emoji_not_found: 404,
  /** 50 distinct emoji per message. */
  reaction_limit: 409,

  // ------------------------------------------------------------ Phase 1 — files (D-010, D-111)
  file_not_found: 404,
  /** `details.maxBytes`. */
  file_too_large: 413,
  /** `files.blockedExtensions`. */
  file_type_blocked: 415,
  /** 60 upload inits per 10 minutes or 20 GiB per day. */
  upload_quota_exceeded: 429,
  /** The upload is not in progress (already completed, aborted or expired). */
  file_not_uploading: 409,
  /** An upload URL writes once: the object is already there (S3 `If-None-Match: *`). */
  upload_already_received: 412,
  upload_incomplete: 422,
  /** The stored object's size is not the declared `sizeBytes`. */
  size_mismatch: 422,
  /**
   * The file is still uploading or processing. 422 when attaching it to a message; the download
   * endpoints answer 409 with the same code (explicit status at the call site, D-113).
   */
  file_not_ready: 422,
  file_quarantined: 409,
  /** Not the uploader's own ready attachment, or already attached elsewhere (D-111). `details.fileId`. */
  file_not_attachable: 422,
  /** `GET /files/:id/snippet` on a file that is not a text snippet (D-1261). `details.fileId`. */
  not_a_snippet: 409,
  /**
   * The streaming download route was asked for a byte range outside the file (D-1104). The reply
   * carries `Content-Range: bytes * /<size>` as RFC 7233 requires.
   */
  range_not_satisfiable: 416,

  // ------------------------------------------------------------ Phase 1 — realtime error frames (PLAN §5)
  /** Not valid JSON, a binary frame, or an envelope that fails the schema. */
  bad_frame: 400,
  unknown_op: 400,
  /** More than 5 viewed conversations, or too many presence ids. */
  too_many_subscriptions: 429,
  ticket_invalid: 403,
  ticket_expired: 401,
  /** The client must resync over REST (`GET /me/bootstrap`). */
  resync_required: 409,
  /** A known op this gateway build does not serve yet (Phase 3 `notification.ack`, D-500). */
  unsupported: 501,

  // ------------------------------------------------------------ Phase 2 — search and navigation
  /** The query cannot be parsed. `details.position` (UTF-16 offset). */
  search_query_invalid: 400,
  /** A modifier matches several people or channels. `details = { token, candidates[] }`. */
  search_filter_ambiguous: 400,

  // ------------------------------------------------------------ Phase 2 — sidebar, Later, reminders, drafts
  section_not_found: 404,
  /** 50 custom sections per member. */
  section_limit: 409,
  saved_item_not_found: 404,
  reminder_not_found: 404,
  /** `POST /reminders/parse` or `/remind` could not find a time. */
  reminder_time_unparsable: 422,
  remind_at_in_past: 422,
  /** Reminders for someone else: only people you share a DM with, never guests. */
  reminder_target_not_allowed: 403,
  scheduled_message_not_found: 404,
  /** `sendAt` must be at least now + 1 minute and at most now + 120 days. */
  send_at_out_of_range: 422,
  /** Only a `scheduled` message can be edited, cancelled or sent now. `details.status`. */
  scheduled_message_not_editable: 409,
  /** 100 scheduled messages per member. */
  scheduled_message_limit: 409,
  /** A newer `clientUpdatedAt` is stored (last writer wins across devices). */
  draft_stale: 409,
  /** `PUT /me/notification-schedule`: overlapping or empty windows. `details.reason`. */
  invalid_schedule: 422,

  // ------------------------------------------------------------ Phase 2 — pins, bookmarks, unfurls
  /** 100 pins per conversation. */
  pin_limit: 409,
  bookmark_not_found: 404,
  /** 50 bookmarks per conversation. */
  bookmark_limit: 409,
  unfurl_not_found: 404,
  /** The URL is refused by the SSRF guard or the domain blocklist (D-104). */
  unfurl_blocked: 422,

  // ------------------------------------------------------------ Phase 2 — user groups and custom emoji
  user_group_not_found: 404,
  group_handle_taken: 409,
  /** A disabled group cannot be mentioned. */
  user_group_disabled: 409,
  /** Also for standard shortcodes. */
  emoji_name_taken: 409,
  /** PNG, GIF or WebP up to 1 MiB. `details.reason`. */
  emoji_file_invalid: 422,

  // ------------------------------------------------------------ Phase 2 — slash commands
  command_not_found: 404,
  /** A bot command name is a built-in or another bot's. */
  command_taken: 409,
  /** The bot did not answer within 3 s. */
  command_timeout: 504,
  /** The bot answered with an error or an invalid body. */
  command_failed: 502,
  /** The response URL is older than 30 minutes or used 5 times. */
  response_url_expired: 410,

  // ------------------------------------------------------------ Phase 2 — bots and webhooks (D-072, D-104)
  bot_not_found: 404,
  bot_handle_taken: 409,
  bot_name_taken: 409,
  bot_token_not_found: 404,
  /** `details.scope`: not in `BOT_SCOPES`, or not in the bot's `maxScopes`. */
  unknown_scope: 422,
  /** Also for a wrong secret (compared in constant time). */
  webhook_not_found: 404,
  webhook_disabled: 410,
  /** The incoming-webhook body is not the documented shape. `details.issues`. */
  payload_invalid: 400,
  /** `details.reason`: not https, a blocked address, or a private range outside the allowlist. */
  url_not_allowed: 422,
  /** `details.eventType`: not an outgoing webhook event of a live phase. */
  unknown_event: 422,
  webhook_delivery_not_found: 404,

  // ------------------------------------------------------------ Phase 2 — import, export, jobs
  import_not_found: 404,
  /** One running import per workspace. */
  import_already_running: 409,
  file_not_zip: 422,
  /** `details.reason` ∈ ARCHIVE_UNSAFE_REASONS. */
  archive_unsafe: 422,
  report_not_ready: 409,
  import_not_in_dry_run: 409,
  export_not_found: 404,
  export_already_running: 409,
  export_not_ready: 409,
  job_not_found: 404,

  // ------------------------------------------------------- Comsky Auth (D-1109)
  /**
   * This deployment has no `COMSKY_CLIENT_ID` / `COMSKY_CLIENT_SECRET`, so nobody can sign in.
   *
   * 503 and not 404: the endpoint exists and the deployment is incomplete, which is an operator's
   * problem to see in a log and a status page, not a caller's to interpret as "no such route".
   */
  auth_not_configured: 503,
  /** The deployment serves several workspaces and the sign-in did not name one. */
  workspace_required: 400,
  /** The provider refused the code, or the exchange failed. Deliberately unspecific. */
  sign_in_failed: 401,
  /**
   * Correct credentials at the wrong door: the account's audience is not this app's `category`.
   *
   * FINAL. Do not retry and do not send the person back round the sign-in — a 401 loop is exactly
   * what this status exists to avoid. Show the message and stop. The message names the app and
   * never the audience: saying "this is a staff account" publishes an internal taxonomy on a
   * customer's first screen and discloses what lives at that address.
   */
  wrong_audience: 403,
  /** The app admits only the people already using it. Treat exactly like `wrong_audience`. */
  app_closed: 403,
  /** The verified identity has no membership and no pending invite (no just-in-time sign-up). */
  not_a_member: 403,
  /** Missing, expired or mismatched `state` / PKCE verifier. */
  sso_state_invalid: 400,
  /** Native: the one-time code is older than 60 s or used. */
  sso_code_expired: 410,

  // ------------------------------------------------- Comsky Store — entitlements (D-1110)
  /**
   * The plan's ceiling for this resource is reached. `details = { quota, limit, used, plan,
   * upgradeUrl }`.
   *
   * Only ever raised against a FRESH entitlement: a stale answer never refuses, because enforcing
   * on one makes a billing outage look like every customer hitting their limit at once.
   */
  quota_exceeded: 403,
  /** The plan does not include this capability. `details = { capability, plan, upgradeUrl }`. */
  capability_not_in_plan: 403,
  /**
   * Every seat the customer is licensed for is filled. `details = { limit, used, plan, upgradeUrl }`.
   *
   * Distinct from `quota_exceeded` because the number comes from the sale's own quantity rather
   * than from a catalogue feature, and the fix is buying seats rather than changing plan — so the
   * two want different words in front of a person.
   */
  seat_limit_reached: 403,
  /** The subscription is read-only. Reads and exports keep working; writes do not. */
  subscription_read_only: 403,
  /** The subscription gives no access at all. */
  subscription_inactive: 403,

  // ------------------------------------------------------------ Phase 4 — tasks (PLAN §3.2, §4)
  /** Unknown task, or one the caller may not see (never 403: existence does not leak, D-037). */
  task_not_found: 404,
  /**
   * The action exists for this state but not for this caller (PLAN §3.2 "Error codes").
   * `details = { from, action, allowedRoles }`.
   */
  transition_forbidden: 403,
  /** `details.fields.dueDate = 'before_today'` or `dueTime = 'before_now'`. */
  due_date_in_past: 422,
  /** The same date and time again (the service may answer a retry as a no-op). */
  due_date_unchanged: 409,
  /** `details = { ownerUserId, suggestedKind: 'decision_request' }` (SPEC §6.3). */
  upward_delegation_requires_decision_request: 422,
  definition_of_done_required: 422,
  /**
   * Decline, cancel and post-acceptance date changes need a reason (Phase 4); so do Partial and
   * Not done checklist outcomes (Phase 5, SPEC §7.5).
   */
  reason_required: 422,
  /** Reopen needs a comment (≥ 3 characters). */
  comment_required: 422,
  /** A push-back needs a proposed date and/or a question. */
  push_back_empty: 422,
  blocker_note_required: 422,
  /** `blockedByTaskId` would make a loop (the service walks ≤ 20 levels). */
  blocked_by_cycle: 422,
  /** Open checklist items; pass `closeOpenChecklistItems`. `details.open`. */
  checklist_incomplete: 409,
  /** Context, 2–6 options, a recommendation and a deadline. `details.fields`. */
  decision_fields_required: 422,
  not_a_decision_request: 422,
  option_not_found: 404,
  /** The owner (or new owner) is deactivated. */
  owner_deactivated: 409,
  /**
   * An owner, watcher, checklist-item owner, blocker or project lead is a bot or the Hub system
   * user: only people can act on a task. `details = { userId, kind }`.
   */
  person_not_human: 422,
  /** `source` names a message the caller cannot read. */
  source_not_visible: 403,
  /** `DELETE /tasks/:id` on a task that left Draft (cancel it instead). */
  not_a_draft: 409,
  /** Done, verified, declined or cancelled: fields are locked (reopen first). */
  task_closed: 409,
  /** A bot supplied `delegatedByUserId` (the delegator is always the bot's owner). */
  delegated_by_not_allowed: 422,
  /** At most 50 checklist items per task. `details.max`. */
  checklist_limit: 409,
  /** A task's checklist item (Phase 4) or a My Checklist item (Phase 5). */
  checklist_item_not_found: 404,
  /** The RecurrenceRule does not validate or expands to nothing. `details.fields`. */
  invalid_recurrence: 422,
  recurrence_not_found: 404,
  project_not_found: 404,
  project_name_taken: 409,
  milestone_not_found: 404,
  /** The milestone belongs to a different project. */
  milestone_project_mismatch: 422,
  delegation_guard_exception_not_found: 404,
  /** An active exception for this pair already exists. */
  exception_exists: 409,
  /** Deactivation with open tasks goes through the offboarding endpoint (D-023). */
  open_tasks_require_reassignment: 409,
  /** `POST /admin/members/:userId/offboard`: blocking items left unmapped. `details.unmapped`. */
  offboarding_incomplete: 409,

  // ------------------------------------------------------------ Phase 5 — PTSTDM (PLAN §4, §3.3 sec. 14)
  // Database guard triggers raise some of these as `P0001` with the code as the message; the API's
  // error handler maps every such code to this status (PTSTDM_TRIGGER_ERRORS, D-250).
  /** A PTSTDM feed or module is not merged yet (tasks: Phase 4; training, meetings: Phase 7). `details.feed`. */
  feature_not_available: 409,
  /** An append-only ledger row cannot change (evidence, verifications, revisions, edit log). */
  append_only_table: 409,
  // positions and assignments
  position_not_found: 404,
  position_code_taken: 409,
  /** The reports-to change would make a seat report to itself (trigger `position_cycle`). */
  reports_to_cycle: 422,
  seat_count_below_holders: 422,
  /** `details.reasons`, e.g. KPI weights ≠ 100. */
  position_incomplete: 422,
  position_has_holders: 409,
  /** Checklists, agendas and pay run only for active positions. */
  position_not_active: 409,
  assignment_not_found: 404,
  seats_full: 409,
  /** Σ allocation of a person's seats > 100 on some day (`details.date`). */
  allocation_exceeds_100: 422,
  /** A second primary seat, or the same seat twice, on overlapping dates. */
  overlapping_assignment: 422,
  cover_is_absent_user: 422,
  /** Guests cannot hold positions. */
  assignment_user_not_eligible: 422,
  kra_not_found: 404,
  kra_name_taken: 409,
  kra_in_use: 409,
  ramp_plan_not_found: 404,
  ramp_plan_name_taken: 409,
  /** `details.issues`: months 1..n contiguous, pct 0–100 non-decreasing. */
  invalid_ramp_steps: 422,
  /** Steps are immutable once a position or assignment uses the plan; clone it. */
  ramp_plan_in_use: 409,
  // recurring tasks and checklists
  recurring_task_not_found: 404,
  /** `details.issues` from the FrequencyRule validator. */
  invalid_frequency: 422,
  kra_not_in_position: 422,
  unknown_trigger_event: 404,
  trigger_event_key_taken: 409,
  /** `details.kind`: the evidence type the item needs. */
  evidence_required: 422,
  /** After the day's report locks a late reason is required. */
  late_reason_required: 422,
  checklist_item_locked: 409,
  not_assignee: 403,
  already_verified: 409,
  maker_checker_same_person: 409,
  not_checker: 403,
  checklist_template_not_found: 404,
  checklist_template_name_taken: 409,
  cannot_start_template: 403,
  /** `details.itemIndexes`. */
  unresolved_assignee: 422,
  checklist_run_not_found: 404,
  checklist_run_not_active: 409,
  // KPIs, scorecards and targets
  kpi_not_found: 404,
  kpi_key_taken: 409,
  /** Ratio parts must be `sum` KPIs and not ratios themselves. */
  ratio_kpis_invalid: 422,
  /** `details.fields`: pay-affecting fields of a KPI in an active scorecard (D-101). */
  kpi_definition_locked: 409,
  scorecard_not_found: 404,
  weights_must_total_100: 422,
  kpi_count_limit_exceeded: 422,
  rubric_incomplete: 422,
  scorecard_must_start_next_period: 422,
  scorecard_immutable: 409,
  /** Withdrawing an active scorecard after targets or entries exist under it (D-1226). */
  scorecard_in_use: 409,
  /** The line's KRA is not one of the scorecard position's KRAs. */
  scorecard_kra_mismatch: 422,
  /** The date is in a closed KPI period (targets, entries, leave, holidays, assignments). */
  period_closed: 409,
  /** The date is in a frozen KPI period (data freeze, SPEC §7.9 "T+N"). */
  period_frozen: 409,
  /** The month's live pay period is computed; use a dispute (Phase 8). */
  pay_inputs_locked: 409,
  /** Targets are versioned: insert a new version instead of editing. */
  target_versioned: 409,
  /** A target for a period that has started needs a reason. */
  target_reason_required: 422,
  target_not_found: 404,
  // KPI entries and verification
  kpi_entry_not_found: 404,
  kpi_not_self_reported: 422,
  /** The period is frozen for pay. */
  entry_locked: 409,
  value_out_of_range: 422,
  kpi_entry_delete_forbidden: 409,
  kpi_entry_identity_immutable: 409,
  discrepancy_open: 409,
  discrepancy_not_found: 404,
  cannot_verify_own: 409,
  /** A correction in the person's favour must be an override (D-099). */
  correction_needs_override: 422,
  not_pending: 409,
  // integration sources and ingestion
  integration_source_not_found: 404,
  integration_source_name_taken: 409,
  unknown_adapter: 422,
  source_token_invalid: 401,
  source_disabled: 403,
  signature_required: 403,
  // daily tracking
  agenda_locked: 409,
  agenda_window_not_open: 409,
  /** `details.kpiIds`: making a target easier needs a reason. */
  target_change_reason_required: 422,
  /** The person is exempt today (leave, holiday, non-working day, no position). */
  exempt_today: 409,
  report_window_not_open: 409,
  reason_required_for_not_done: 422,
  /** Achievement below the department threshold (default 70 %) needs a reason. */
  low_achievement_reason_required: 422,
  report_locked_for_pay: 409,
  report_not_submitted: 409,
  /** The daily channel must be a public channel. */
  channel_not_public: 422,
  /** The department head must be an active member (not a guest). */
  department_head_not_eligible: 422,
  // import wizard
  unreadable_spreadsheet: 422,
  mapping_invalid: 422,
  mapping_incomplete: 409,
  dry_run_has_errors: 409,
  /** `details.reason`: `mapping_changed` or `data_changed`. */
  dry_run_stale: 409,
  import_not_ready: 409,
  import_preset_not_found: 404,
  // holidays and leave
  holiday_not_found: 404,
  holiday_exists: 409,
  leave_not_found: 404,
  overlapping_leave: 422,
  /** People do not mark their own leave in v1 (it exempts them from targets). */
  cannot_mark_own_leave: 403,
  leave_not_active: 409,

  // ------------------------------------------------------------ Phase 3 — push, home, read receipts
  /** `POST /me/devices`: the token does not look like a token of that kind. */
  invalid_push_token: 422,
  /** Unknown push device, or one that belongs to another session or person. */
  push_device_not_found: 404,
  /**
   * `GET /me/web-push/config`: the server's VAPID key pair cannot be created or opened (no
   * `SECRETS_ENCRYPTION_KEY` in production, D-751). Web Push is off; the app hides its control.
   */
  web_push_unavailable: 503,

  // ------------------------------------------------------------ Phase 6 — calls and huddles (PLAN §4)
  /** Unknown call, or one in a conversation the caller cannot read (never 403: existence does not leak). */
  call_not_found: 404,
  /** `calls.enabled` is off for this workspace. */
  calls_disabled: 403,
  /** LiveKit is not configured on this server (`GET /calls/availability` says why). */
  calls_unavailable: 503,
  /** The conversation already has a live call or huddle. `details.callId`: join that call. */
  call_already_active: 409,
  /** Accept, decline or cancel on a call that is not ringing (another device answered, or it ended). */
  call_not_ringing: 409,
  /** A token or join for a call that has ended. */
  call_ended: 409,
  /** The room is at `calls.maxParticipants` (50 at most). `details.max`. */
  call_full: 409,
  /** The action needs the caller to be in the call right now. */
  not_in_call: 403,
  /** Self DMs and task conversations never host a call. `details.kind`. */
  call_not_supported: 422,
  /** `ringUserIds` is for channel calls; DMs and group DMs ring their own members. */
  ring_users_not_allowed: 422,

  // ------------------------------------------------------------ Wave 4 — skills (Phase 7, D-810 ff.)
  skill_not_found: 404,
  skill_name_taken: 409,
  skill_category_not_found: 404,
  skill_category_name_taken: 409,
  /** Assessing an archived skill. */
  skill_archived: 409,
  /** A manager rating by someone who is not in the person's manager chain. */
  not_in_chain: 403,
  skill_proposal_not_found: 404,
  /** Confirm or reject on a proposal that is already decided or superseded. */
  skill_proposal_not_pending: 409,

  // ------------------------------------------------------------ Wave 4 — training (D-820 ff.)
  training_module_not_found: 404,
  training_module_title_taken: 409,
  /** Publishing a module whose assessment is `mcq` and that has no quiz. */
  quiz_required: 422,
  /** The quiz does not validate (`details.issues`): a single question needs exactly one correct option. */
  quiz_invalid: 422,
  /** An attempt on a module that is not published. */
  module_not_published: 409,
  max_attempts_reached: 409,
  attempt_not_found: 404,
  /** The attempt's time limit passed before it was submitted. */
  attempt_expired: 410,
  attempt_already_submitted: 409,
  training_assignment_not_found: 404,
  /** Start, complete, sign off or waive an assignment that is not open. */
  assignment_not_open: 409,
  /** `complete` is only for modules without an assessment. */
  assessment_required: 409,
  /** A practical sign-off by someone who is not the module's trainer. */
  not_trainer: 403,
  /** A certificate for an assignment that is not completed. */
  not_completed: 409,
  /** The certificate PDF has not been rendered yet; try again shortly. */
  certificate_not_ready: 409,

  // ------------------------------------------------------------ Wave 4 — weekly reviews and meetings (D-840, D-850 ff.)
  weekly_review_not_found: 404,
  /** No review has been generated for that week (before the Sunday job, or before the person joined). */
  weekly_review_not_generated: 404,
  meeting_series_not_found: 404,
  meeting_occurrence_not_found: 404,
  meeting_agenda_item_not_found: 404,
  meeting_note_not_found: 404,
  meeting_decision_not_found: 404,
  meeting_issue_not_found: 404,
  meeting_action_item_not_found: 404,
  /** The locked `dept_tactical` agenda cannot have sections added, removed or reordered. */
  agenda_template_locked: 422,
  /** A call from a meeting that has no linked conversation. */
  no_linked_conversation: 422,
  transcript_not_found: 404,
  /** The pasted or uploaded transcript cannot be read. `details.reason`. */
  transcript_unparsable: 422,
  /** An extraction is already running for this transcript. */
  transcript_busy: 409,

  // ------------------------------------------------------------ Wave 4 — appraisal (D-860 ff.)
  appraisal_form_not_found: 404,
  /** Edits to a published form: it is immutable, create the next version. */
  appraisal_form_published: 409,
  /** The form does not validate (`details.issues`). */
  appraisal_form_invalid: 422,
  appraisal_cycle_not_found: 404,
  /** One cycle per type and KPI period. */
  appraisal_cycle_exists: 409,
  appraisal_not_found: 404,
  /** Saving or submitting a review section that is already submitted. */
  section_submitted: 409,
  cycle_locked: 409,
  /** `details.questionIds`: required questions without an answer. */
  required_answers_missing: 422,
  /** Starting calibration or locking while reviews are unsubmitted. `details.count`. */
  reviews_incomplete: 409,
  cycle_not_in_calibration: 409,

  // ------------------------------------------------------------ Wave 4 — AI, drafts only (D-870 ff.)
  /** `ai.enabled` or the feature's flag is off. `details.feature`. */
  ai_disabled: 403,
  /** The monthly cap is reached. `details.resetsAt`. */
  ai_budget_exceeded: 429,
  /** No `ANTHROPIC_API_KEY` on the server, or the provider is unreachable. `details.reason`. */
  ai_unavailable: 503,
  /** The model output failed its schema twice (nothing was stored as a draft). */
  ai_output_invalid: 422,
  ai_draft_not_found: 404,
  /** Edit, approve or discard a draft that is no longer pending. */
  draft_not_pending: 409,

  // ------------------------------------------------------------ Wave 4 — variable pay (Phase 8, D-880 ff.)
  /** The sealing key (`COMP_DATA_KEY`) is not configured, or cannot open the data key. */
  pay_vault_unavailable: 503,
  /** The caller's own record, assignment, plan, dispute, inputs or computation is involved (D-098). */
  self_approval_forbidden: 403,
  /** A write to a pay-affecting setting the caller may not make (D-097). */
  setting_not_writable: 403,
  /** A salary dated inside a measurement period whose pay period has left `open`. */
  period_governed: 409,
  pay_plan_not_found: 404,
  plan_name_taken: 409,
  pay_plan_version_not_found: 404,
  /** The plan version does not validate: `details.issues`. */
  plan_invalid: 422,
  /** Edits to a version that is published. */
  plan_version_published: 409,
  /** Publishing with an `effectiveFrom` that is not a not-yet-started measurement period. */
  retroactive_effective_date: 422,
  component_weights_not_100: 422,
  /** An assignment or end date before today. */
  assignment_date_in_past: 422,
  /** The letter PDF is not rendered yet. */
  letter_not_ready: 409,
  already_acknowledged: 409,
  simulation_input_invalid: 422,
  pay_period_not_found: 404,
  /** `details.count`: managers clear their verifications first. */
  pending_verifications: 409,
  period_locked: 409,
  review_window_closed: 409,
  dispute_exists: 409,
  pay_dispute_not_found: 404,
  pay_result_not_found: 404,
  pay_gate_override_not_found: 404,
  pay_adjustment_not_found: 404,
  /** The period has passed `disputes_resolution`: use the next open period. */
  period_not_adjustable: 409,
  pay_adjustment_shadow_mismatch: 422,
  /** Approver is the requester (or the subject): another approver is needed. */
  four_eyes_required: 409,
  disputes_open: 409,
  /** A person or the period is above the Owner threshold: the Owner must also approve. */
  owner_approval_required: 409,
  /** Something the approval is bound to moved since the computation. */
  inputs_changed_since_compute: 409,
  acknowledgements_missing: 409,
  pending_input_change_not_found: 404,
  periods_not_locked: 409,
  pay_export_not_found: 404,
  /** The Owner's approval was bound to a different `items_hash`. */
  export_items_changed: 409,
  export_not_generated: 409,
  /** The encrypted file was purged after the retention period; regenerate it. */
  export_file_purged: 410,
  grant_not_allowed_for_guest: 422,

  // ------------------------------------------------------------ Wave 4 — optional antivirus (Phase 9, D-910 ff.)
  /** Switching scanning on while the server has no scanner (`AV_SCAN_ENABLED`, a reachable clamd). */
  antivirus_unavailable: 409,

  // ------------------------------------------------------------ Wave 5 — calendar, guests, webinars, booking pages (D-1000 ff.)
  // Public routes (`/api/v1/public/*`) answer one identical `404 not_found` for every unknown,
  // expired, revoked, removed, disabled or switched-off token or slug (D-1036); the codes below are
  // for authenticated callers and for the states a valid token may meet.
  meeting_guest_not_found: 404,
  webinar_not_found: 404,
  webinar_question_not_found: 404,
  booking_page_not_found: 404,
  booking_not_found: 404,
  lobby_entry_not_found: 404,
  /** No such participant (identity) in the occurrence's live meeting room. */
  meeting_participant_not_found: 404,
  calendar_feed_not_found: 404,
  /** A guest removed from a meeting occurrence cannot join it again (D-1028). */
  removed_from_meeting: 403,
  /** Guests only on `general` and `webinar` meetings (D-1011). */
  guest_not_allowed_for_type: 422,
  /** `calendar.externalGuestsEnabled` is off (D-1048). */
  external_guests_disabled: 422,
  /** More than `calendar.maxGuestsPerMeeting` (≤ 50) guests on one meeting. `details.max`. */
  too_many_guests: 422,
  /** Guests on a recurring series whose rule iCalendar cannot express safely (D-1017). `details.reason`. */
  recurrence_not_shareable: 422,
  /** A just-this-one reschedule must stay strictly between the neighbouring occurrences (D-1008). */
  reschedule_out_of_order: 422,
  /** A time change of a one-off meeting goes through the occurrence (`PATCH /meeting-occurrences/:id`). */
  edit_the_occurrence: 422,
  /** `nominalDate` is not a slot of the series' plan, or is beyond now + 400 days (D-1007). */
  occurrence_date_not_planned: 422,
  /** Only future `scheduled` occurrences can be edited or rescheduled. */
  occurrence_not_editable: 409,
  /** A `presenter` or `attendee` role on a non-webinar series, or `required`/`optional` on a webinar (trigger). */
  attendee_role_not_allowed: 422,
  /** The slot is taken or no longer offered (booking rules and `bookings_no_overlap`, D-1035). */
  slot_unavailable: 409,
  /** The page's daily cap tripped: new bookings wait for the next local day (D-1035). */
  booking_page_busy: 429,
  /** Cancelling or rescheduling a booking inside the page's minimum notice. */
  booking_change_too_late: 409,
  /** A team page host outside the creator's reporting chain (admins excepted). `details.userIds`. */
  booking_host_not_allowed: 422,
  /** `bookings.enabled` is off (D-1048). */
  bookings_disabled: 422,
  /** `webinars.enabled` is off (D-1048). */
  webinars_disabled: 422,
  /** No seat left and no waitlist, or the 2 × capacity registration cap is reached (D-1032). */
  webinar_full: 409,
  /** Outside the webinar's registration window, or the page is closed. */
  webinar_registration_closed: 409,
  /** More than 10 presenters, or more than 5 outside presenters (D-1031). `details.max`. */
  too_many_presenters: 422,
  /** Lowering a webinar's capacity below the seats already held. `details.held`. */
  capacity_below_registrations: 409,
  /** A webinar audience member asked to join before a presenter started the room (D-1028). */
  meeting_not_started: 409,
  /** Outside the join window (15 min before the start to 30 min after the end), unless the room is live. */
  join_window_closed: 409,
  /** A denied guest asked again more than 3 times for this occurrence (D-1028). */
  lobby_retry_limit: 429,
  /** A token or room action for a guest session that is not admitted to the live call. */
  guest_session_not_admitted: 409,
  /** Re-showing a guest link made with another `CALENDAR_LINK_SECRET`: reset the link (D-1012). */
  guest_link_reset_needed: 409,
  /** Email is not deliverable on this server (`MAIL_TRANSPORT`, D-1021). */
  mail_not_deliverable: 409,
  /** `calendar.feedsEnabled` is off (D-1048). */
  feeds_disabled: 422,
  /** A public form ticket is missing, expired, used up or from another network: reload the page (D-1036). */
  form_expired: 422,
  /** Workday start must be before the end and at least 60 minutes apart (D-1025). `details.fields`. */
  invalid_workday_hours: 422,
} as const satisfies Record<string, number>;

/**
 * Error codes that Phase 5 guard triggers raise as `RAISE EXCEPTION '<code>' USING ERRCODE =
 * 'P0001'` (`packages/db/migrations/*_phase5_custom.sql`), with the code the API answers when the
 * trigger's message differs from the public code.
 */
export const PTSTDM_TRIGGER_ERRORS: Readonly<Record<string, ErrorCodeLiteral>> = Object.freeze({
  append_only_table: 'append_only_table',
  period_closed: 'period_closed',
  period_frozen: 'period_frozen',
  pay_inputs_locked: 'pay_inputs_locked',
  position_cycle: 'reports_to_cycle',
  allocation_exceeds_100: 'allocation_exceeds_100',
  ramp_plan_in_use: 'ramp_plan_in_use',
  kpi_definition_locked: 'kpi_definition_locked',
  weights_must_total_100: 'weights_must_total_100',
  kpi_count_limit_exceeded: 'kpi_count_limit_exceeded',
  scorecard_immutable: 'scorecard_immutable',
  target_versioned: 'target_versioned',
  kpi_entry_delete_forbidden: 'kpi_entry_delete_forbidden',
  kpi_entry_identity_immutable: 'kpi_entry_identity_immutable',
  invalid_transition: 'invalid_transition',
  // Wave 5 guards (0029_calendar_custom.sql).
  attendee_role_not_allowed: 'attendee_role_not_allowed',
  guest_not_allowed_for_type: 'guest_not_allowed_for_type',
  webinar_full: 'webinar_full',
  webinar_type_mismatch: 'validation_failed',
  delivery_sequence_regressed: 'invalid_transition',
  delivery_identity_immutable: 'invalid_transition',
  call_not_supported: 'call_not_supported',
  call_identity_immutable: 'invalid_transition',
});

/**
 * Exclusion constraints (SQLSTATE `23P01`) of Phase 5 and the code the API answers.
 */
export const PTSTDM_EXCLUSION_ERRORS: Readonly<Record<string, ErrorCodeLiteral>> = Object.freeze({
  leaves_no_overlap: 'overlapping_leave',
  position_assignments_one_primary: 'overlapping_assignment',
  position_assignments_no_double_seat: 'overlapping_assignment',
  integration_user_links_no_overlap: 'validation_failed',
  // Wave 5 (D-1035): held and confirmed bookings of one host never overlap.
  bookings_no_overlap: 'slot_unavailable',
});

type ErrorCodeLiteral = keyof typeof ERROR_STATUS;

export type ErrorCode = keyof typeof ERROR_STATUS;
export const ERROR_CODES = Object.keys(ERROR_STATUS) as ErrorCode[];

/** Named constants, e.g. `ErrorCodes.not_found === 'not_found'`. */
export const ErrorCodes = Object.freeze(
  Object.fromEntries(ERROR_CODES.map((code) => [code, code])) as { [K in ErrorCode]: K },
);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(ERROR_STATUS, value);
}

export function statusForErrorCode(code: ErrorCode): number {
  return ERROR_STATUS[code];
}

/** One entry of `details.issues` on a `validation_failed` error. */
export const validationIssueSchema = z.object({
  /** Dotted path, prefixed with the request part: `body.email`, `query.limit`, `params.id`. */
  path: z.string(),
  message: z.string(),
  code: z.string(),
});
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

/** `details` of a `429 rate_limited` error. */
export const rateLimitDetailsSchema = z.object({
  limit: z.number().int().positive(),
  windowSeconds: z.number().int().positive(),
  retryAfterSeconds: z.number().int().positive(),
});
export type RateLimitDetails = z.infer<typeof rateLimitDetailsSchema>;

/**
 * The error envelope. `code` is parsed as a plain string so older clients keep working when the
 * server adds codes; server code should only ever emit an {@link ErrorCode}.
 */
export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;
